import { useEffect, useRef, useState } from "react";

/** A small picture is enlarged to fill the stage, but no further than this. */
const MAX_UPSCALE = 2;

/**
 * A shared image, fitted whole into the stage.
 *
 * Sized here rather than left to `object-fit`: an `<img>` with `object-fit:
 * contain` keeps its box the size of the stage and letterboxes the picture
 * inside it, and an annotation layer over that box would let ink land on the
 * bars. The box below is exactly the picture, so `renderOverlay` gets a box
 * that matches it — the same contract as a page of SharedPdfViewer.
 *
 * Give it `key={url}`: the measured size belongs to one picture.
 */
export function SharedImageViewer({
  url,
  name,
  renderOverlay,
}: {
  url: string;
  name: string;
  /** Drawn over the image, inside a box its exact size. Gets page 1 and the
   *  image's height over width. */
  renderOverlay?: (page: number, ratio: number) => React.ReactNode;
}) {
  const areaRef = useRef<HTMLDivElement>(null);
  const [area, setArea] = useState({ w: 0, h: 0 });
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const el = areaRef.current;
    if (!el) return;
    const measure = () => setArea({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const scale = natural && area.w && area.h
    ? Math.min(area.w / natural.w, area.h / natural.h, MAX_UPSCALE)
    : 0;

  return (
    <div className="absolute inset-0 p-4">
      <div ref={areaRef} className="flex h-full w-full items-center justify-center">
        {failed ? (
          <p className="text-sm text-slate-500 dark:text-slate-400">This image could not be opened.</p>
        ) : (
          <div
            className="relative shrink-0 bg-white shadow"
            style={scale ? { width: natural!.w * scale, height: natural!.h * scale } : { visibility: "hidden" }}
          >
            <img
              src={url}
              alt={name}
              className="block h-full w-full"
              referrerPolicy="strict-origin-when-cross-origin"
              draggable={false}
              onLoad={(e) => {
                const img = e.currentTarget;
                if (img.naturalWidth && img.naturalHeight) setNatural({ w: img.naturalWidth, h: img.naturalHeight });
              }}
              onError={() => setFailed(true)}
            />
            {scale > 0 && renderOverlay?.(1, natural!.h / natural!.w)}
          </div>
        )}
      </div>
    </div>
  );
}
